import {
	type CaptureFileJSONObject,
	type CollectionFieldVectorFieldValue,
	type CollectionModelFieldAnchorValue,
	IkeFieldType,
	IkeUnitType,
	type LocationData,
	MarkerType,
} from '@ikegps/core-api';
import { CollectionModel } from '../collection.data';
import type { CollectionModelDrawableState } from '../drawable/drawable.state';
import type { CollectionModelFieldDate } from './date.field';
import type { CollectionModelFieldFileTool } from './file.tool.field';
import { IkeUnit } from '../../../unit';
import type { FormModelField } from '../../form/form.field';
import { CollectionModelField, ModelMutator } from '../collection.field';
import { CollectionModelDrawableMarker } from '../drawable/marker.drawable';
import { CollectionModelDrawableVerticalSeparation } from '../drawable/vertical.separation.drawable';
import { registerFieldType } from './field.registry';
import { CollectionModelFieldLocation } from './location.field';
import type { CaptureProvider } from './provider/capture.provider';

export interface ModelFieldStateVerticalSeparation {
	value?: number;
	drawableTop?: string;
	drawableBottom?: string;
	captureId?: string;
	drawableVerticalSeparation?: string;
}

export class ModelMutatorVerticalSeparation extends ModelMutator<
	FormModelField,
	CollectionModelFieldVerticalSeparation
> {
	manual(newValue: number | null) {
		this.unlink();

		if (newValue == null || isNaN(newValue)) {
			this.state.setFieldValue<ModelFieldStateVerticalSeparation>(this.field.id, null);
		} else {
			this.state.setFieldValue<ModelFieldStateVerticalSeparation>(this.field.id, {
				value: newValue,
			});
		}
	}

	override clear() {
		this.manual(null);
	}

	drawable(marker: CollectionModelDrawableMarker) {
		// If this is the first marker to be linked,
		// or an existing top marker link is in a different Capture,
		// then set a new top marker with no bottom marker.
		if (!this.field.stateValue?.drawableTop || marker.captureId !== this.field.stateValue.captureId) {
			this.state.setFieldValue(this.field.id, {
				drawableTop: marker.id,
				drawableBottom: '',
				captureId: marker.captureId,
			});
			return;
		}

		// If the top has already been linked then set the bottom marker
		// and create/update the Vertical Separation Drawable.
		const drawable = this.field.drawable
			? this.field.drawable
			: CollectionModelDrawableVerticalSeparation.createWithLabel(
					this.field.collection.getDrawableState(marker.captureId),
					this.field.stateValue.drawableTop,
					marker.id,
					this.field.id
				);

		this.state.setFieldValue(this.field.id, {
			drawableBottom: marker.id,
			drawableTop: this.field.stateValue.drawableTop,
			captureId: marker.captureId,
			drawableVerticalSeparation: drawable.id,
		});
	}

	// This method is used to set the top / bottom drawables and label states
	// directly, during deserialization.
	setDrawables(
		drawableTop: CollectionModelDrawableMarker,
		drawableBottom?: CollectionModelDrawableMarker,
		sep?: CollectionModelDrawableVerticalSeparation
	) {
		this.state.setFieldValue(this.field.id, {
			drawableTop: drawableTop.id,
			drawableBottom: drawableBottom?.id,
			captureId: drawableTop.captureId,
			drawableVerticalSeparation: sep?.id,
		});
	}

	unlink() {
		// Only unlink if currently linked.
		if (
			!this.field.stateValue?.drawableTop &&
			!this.field.stateValue?.drawableBottom &&
			!this.field.stateValue?.drawableVerticalSeparation
		) {
			return;
		}

		const stateValue = this.field.stateValue;
		if (stateValue == null || stateValue.drawableTop == null || stateValue.captureId == null) {
			// Partial state — remove VS drawable if accessible, but nothing to unlink
			this.field.drawable?.remove();
			return;
		}

		this.state.setFieldValue(this.field.id, null);

		const drawableState = this.field.collection.getDrawableState(stateValue.captureId);

		// Determine and clean up the VS drawable BEFORE unlinking the marker labels.
		// Otherwise, when this field is the last link on a marker's label, the marker
		// label's `unlink` cascades to remove the marker — which would leave the VS
		// drawable in the state pointing at a missing marker. A render reaction can
		// then fire on the inconsistent state (VS still in the map, marker gone) and
		// crash on `getDrawable(topMarkerId)`.
		//
		// Only remove the VS drawable if no other field still references it.
		// During same-collection copy (duplication), the VS drawable is shared between
		// the original and duplicate. Removing it here would destroy the original's measurement.
		if (stateValue.drawableVerticalSeparation && drawableState.hasDrawable(stateValue.drawableVerticalSeparation)) {
			const isStillReferenced =
				drawableState.hasDrawable(stateValue.drawableTop) &&
				drawableState
					.getDrawable<CollectionModelDrawableMarker>(stateValue.drawableTop)
					.label.links.some(
						(link) =>
							CollectionModelFieldVerticalSeparation.isVerticalSeparationField(link) &&
							link.stateValue?.drawableVerticalSeparation === stateValue.drawableVerticalSeparation
					);

			if (!isStillReferenced) {
				drawableState
					.getDrawable<CollectionModelDrawableVerticalSeparation>(stateValue.drawableVerticalSeparation)
					.remove();
			}
		}

		// Unlink this field from the top and bottom markers
		if (drawableState.hasDrawable(stateValue.drawableTop)) {
			const drawableTop = drawableState.getDrawable<CollectionModelDrawableMarker>(stateValue.drawableTop);
			drawableTop.label.unlink(this.field.id);
		}

		if (stateValue.drawableBottom && drawableState.hasDrawable(stateValue.drawableBottom)) {
			const drawableBottom = drawableState.getDrawable<CollectionModelDrawableMarker>(stateValue.drawableBottom);
			drawableBottom.label.unlink(this.field.id);
		}
	}

	private linkExistingMarkerOrCopy(
		destinationDrawableState: CollectionModelDrawableState,
		originalDrawable: CollectionModelDrawableMarker
	) {
		let myCopiedDrawable: CollectionModelDrawableMarker | null = null;
		// if original drawable is linked to multiple fields, check if already copied
		if (originalDrawable.label.links.length > 1) {
			const existingCopiedDrawable = destinationDrawableState
				.getAllDrawables()
				.filter(
					(copiedDrawable) =>
						copiedDrawable instanceof CollectionModelDrawableMarker && copiedDrawable.y === originalDrawable.y
				)[0];

			myCopiedDrawable = existingCopiedDrawable ? (existingCopiedDrawable as CollectionModelDrawableMarker) : null;
		}

		// create new drawable if not already existing
		if (!myCopiedDrawable) {
			myCopiedDrawable = originalDrawable.clone(destinationDrawableState);
		}

		return myCopiedDrawable;
	}

	override copy(from: CollectionModelFieldVerticalSeparation): boolean {
		if (!from.stateValue) {
			this.clear();
			return true;
		}

		// Clean up existing drawables before copying new ones.
		// Without this, a re-link leaves orphaned VS drawables in the drawable state
		// which causes errors when sorting by field value (stale _fieldId references).
		this.unlink();

		const fromCollection = from.collection;
		const toCollection = this.field.collection;
		const captureId = from.stateValue.captureId;

		// Do not copy if missing capture or if source field is not fully linked to top and bottom markers
		if (
			!captureId ||
			!from.stateValue.drawableTop ||
			!from.stateValue.drawableBottom ||
			!from.stateValue.drawableVerticalSeparation
		) {
			return false;
		}

		const fromDrawableState = fromCollection.getDrawableState(captureId);

		const drawableTop = fromDrawableState.getDrawable<CollectionModelDrawableMarker>(from.stateValue.drawableTop);
		const drawableBottom = fromDrawableState.getDrawable<CollectionModelDrawableMarker>(from.stateValue.drawableBottom);
		const drawable = fromDrawableState.getDrawable<CollectionModelDrawableVerticalSeparation>(
			from.stateValue.drawableVerticalSeparation
		);

		// If copying to another collection, try to link to existing markers at the same heights before creating new ones.
		if (fromCollection !== toCollection) {
			const toDrawableState = toCollection.getDrawableState(captureId);
			if (toDrawableState.base == null) {
				this.copyBaseMarkers(fromCollection, captureId);
			}
			const copyDrawableTop = this.linkExistingMarkerOrCopy(toDrawableState, drawableTop);
			const copyDrawableBottom = this.linkExistingMarkerOrCopy(toDrawableState, drawableBottom);
			const copyDrawable = drawable.clone(toDrawableState, this.field.id, copyDrawableTop.id, copyDrawableBottom.id);

			this.setDrawables(copyDrawableTop, copyDrawableBottom, copyDrawable);
			copyDrawableTop.label.linkVerticalSeparation(this.field);
			copyDrawableBottom.label.linkVerticalSeparation(this.field);

			return true;
		}

		// Otherwise just reuse the existing markers by adding a new link to the existing markers to the field being copied into.
		this.setDrawables(drawableTop, drawableBottom, drawable);
		drawableTop.label.linkVerticalSeparation(this.field);
		drawableBottom.label.linkVerticalSeparation(this.field);

		return true;
	}

	// duplicated in height.field.ts
	private copyBaseMarkers(fromCollection: CollectionModel, captureId: string) {
		const collection = this.field.collection;

		const drawableState = collection.getDrawableState(captureId);
		const fromDrawableState = fromCollection.getDrawableState(captureId);

		const fromBaseMarker = fromDrawableState.base;
		if (fromBaseMarker != null) {
			const { marker: baseMarker } = CollectionModelDrawableMarker.createWithLabel(
				drawableState,
				fromBaseMarker.getVector(),
				fromBaseMarker.label.getVector(),
				undefined,
				MarkerType.BASE
			);

			const fromBaseOffsetMarker = fromDrawableState.baseOffset;
			if (fromBaseOffsetMarker != null) {
				baseMarker.offset = fromBaseMarker.offset;
			}
		}
	}
}

export class CollectionModelFieldVerticalSeparation
	extends CollectionModelField<FormModelField, ModelFieldStateVerticalSeparation>
	implements CaptureProvider
{
	static TYPE: IkeFieldType = IkeFieldType.VERTICAL_SEPARATION;
	static isVerticalSeparationField(f: CollectionModelField): f is CollectionModelFieldVerticalSeparation {
		return f.schema.type === CollectionModelFieldVerticalSeparation.TYPE;
	}

	private _Mutator: ModelMutatorVerticalSeparation = new ModelMutatorVerticalSeparation(this);

	get Mutator() {
		return this._Mutator;
	}

	hasValue(): boolean {
		const stateValue = this.stateValue;
		if (stateValue == null) {
			return false;
		}
		if (!stateValue.drawableTop || !stateValue.drawableBottom) {
			return false;
		}
		return true;
	}

	hasDrawableTop(): boolean {
		const stateValue = this.stateValue;
		if (stateValue == null || !stateValue.drawableTop) {
			return false;
		}
		return true;
	}

	hasDrawableBottom(): boolean {
		const stateValue = this.stateValue;
		if (stateValue == null || !stateValue.drawableBottom) {
			return false;
		}
		return true;
	}

	override get value() {
		const stateValue = this.stateValue;
		if (stateValue == null) {
			return null;
		}
		if (this.drawableTop?.value == null || this.drawableBottom?.value == null) {
			return null;
		}
		return this.drawableTop.value - this.drawableBottom.value;
	}

	get drawable(): CollectionModelDrawableVerticalSeparation | null {
		if (!this.stateValue?.captureId || !this.stateValue?.drawableVerticalSeparation) {
			return null;
		}
		const drawableState = this.collection.getDrawableState(this.stateValue.captureId);
		return drawableState.getDrawable<CollectionModelDrawableVerticalSeparation>(
			this.stateValue.drawableVerticalSeparation
		);
	}

	get drawableTop(): CollectionModelDrawableMarker | null {
		if (!this.stateValue?.captureId || !this.stateValue?.drawableTop) {
			return null;
		}
		const drawableState = this.collection.getDrawableState(this.stateValue.captureId);
		return drawableState.getDrawable<CollectionModelDrawableMarker>(this.stateValue.drawableTop);
	}

	get drawableBottom(): CollectionModelDrawableMarker | null {
		if (!this.stateValue?.captureId || !this.stateValue?.drawableTop || !this.stateValue?.drawableBottom) {
			return null;
		}
		const drawableState = this.collection.getDrawableState(this.stateValue.captureId);
		const d = drawableState.getDrawable<CollectionModelDrawableMarker>(this.stateValue.drawableBottom);
		const top = drawableState.getDrawable<CollectionModelDrawableMarker>(this.stateValue.drawableTop);
		top.pair = addPair(updatePair(top.pair), d.id);
		d.pair = addPair(updatePair(d.pair), top.id);
		return d;
	}

	get clearanceThresholdInMeters(): number | null {
		return this.schema.clearanceThresholdInMeters ?? null;
	}

	override get annotationLabel(): string {
		//if we're self-title no need to search for field just return displayname
		const fieldId = this.schema.annotationLabelFieldId;
		if (fieldId === this.schema.id) {
			return this.schema.displayName;
		}

		const field = fieldId ? this.findAncestorField(this.parent, fieldId) : null;
		if (!field || !field.schema.type) {
			return this.parent.title ?? '';
		}

		switch (field.schema.type) {
			case IkeFieldType.ANCHOR:
				// I think to get the complex values we have to type them first and put them together ourselves
				if (field.hasValue()) {
					let anchor: CollectionModelFieldAnchorValue = field.value as CollectionModelFieldAnchorValue;
					return `${anchor.collectionTool} ${anchor.placement}`;
				}
				return field.schema.displayName;
			case IkeFieldType.DATE:
				let date: CollectionModelFieldDate = field as CollectionModelFieldDate;
				if (!date.hasValue() || date.displayValue == null || date.displayValue === '') {
					return this.parent.title ?? '';
				}
				return date.displayValue;
			case IkeFieldType.FILE_TOOL:
				let fileTool: CollectionModelFieldFileTool = field as CollectionModelFieldFileTool;
				if (!fileTool.hasValue() || fileTool.displayValue == null || fileTool.displayValue === '') {
					return this.parent.title ?? '';
				}
				return fileTool.displayValue;
			case IkeFieldType.FORM:
				return field.schema.displayName;
			case IkeFieldType.HEIGHT:
				return field.schema.displayName;
			case IkeFieldType.IMAGE:
				return field.schema.displayName;
			case IkeFieldType.LOCATION:
				let loc: CollectionModelFieldLocation = field as CollectionModelFieldLocation;
				if (!loc.hasValue() || loc.displayValue == null || loc.displayValue === '') {
					return this.parent.title ?? '';
				}
				let locVal = loc.value as LocationData;
				return `${locVal.latitude.toFixed(2)},${locVal.longitude.toFixed(2)}`;
			case IkeFieldType.NESTED_LIST:
				if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
					return this.parent.title ?? '';
				}
				return field.displayValue;
			case IkeFieldType.NUMBER:
				if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
					return this.parent.title ?? '';
				}
				return field.displayValue;
			case IkeFieldType.SELECT_LIST:
				if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
					return this.parent.title ?? '';
				}
				return field.displayValue;
			case IkeFieldType.TEXT:
				if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
					return this.parent.title ?? '';
				}
				return field.displayValue;
			case IkeFieldType.TRUE_SIZE:
				return field.schema.displayName;
			case IkeFieldType.VECTOR:
				let vector: CollectionFieldVectorFieldValue = field.value;
				if (field.hasValue()) {
					return `${IkeUnit.display(vector.distance, this.distanceUnit, IkeUnit.METER, IkeUnitType.DISTANCE, 1)} ${IkeUnit.display(vector.bearing, IkeUnit.DEGREE, IkeUnit.RADIAN, IkeUnitType.ANGLE, 1)}`;
				}
				return field.schema.displayName;
			case IkeFieldType.VERTICAL_SEPARATION:
				return field.schema.displayName;
			case IkeFieldType.YES_NO:
				return field.schema.displayName;
			default:
				return this.parent.title ?? '';
		}
	}

	getOwnedCaptures(): CaptureFileJSONObject[] {
		return [];
	}

	getCaptures(): CaptureFileJSONObject[] {
		return this.getCaptureIds()
			.map((c) => this.collection.state.getCapture(c))
			.filter((c) => !!c);
	}

	getOwnedCaptureIds(): string[] {
		return this.getCaptureIds();
	}
	getCaptureIds(): string[] {
		return [];
	}
}

const updatePair = (pair: string | string[] | undefined): string[] | undefined => {
	if (typeof pair === 'string') {
		return [pair];
	}
	return pair;
};

const addPair = (pair: string[] | undefined, newPairedId: string): string[] => {
	if (pair == null) {
		return [newPairedId];
	}
	if (pair.includes(newPairedId)) {
		return pair;
	}
	return [...pair, newPairedId];
};

registerFieldType(
	IkeFieldType.VERTICAL_SEPARATION,
	(parent, schema) => new CollectionModelFieldVerticalSeparation(parent, schema)
);
